import React, {useEffect, useState} from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {BulkActionChanges, BulkActionModalProps} from './BulkActionModal.props';
import './BulkActionModal.styles.css';
import {Button} from '../../atoms/Button';
import {BULK_ACTION, BULK_ACTION_APPLIED_TO_ALL, UNCHANGED_OPTION} from '../../../constants/contants';

export const BULK_ACTION_PRIORITY_OPTIONS = [
  UNCHANGED_OPTION,
  'High',
  'Medium',
  'Low',
  'Unknown'
];
export const BULK_ACTION_STATUS_OPTIONS = [UNCHANGED_OPTION, 'Received', 'In Review', 'Resolved'];
export const BULK_ACTION_CATEGORY_OPTIONS = [UNCHANGED_OPTION, 'Threat', 'Clean', 'Unknown'];

type BulkActionField = 'priority' | 'status' | 'category';

const BULK_ACTION_FIELDS: Record<BulkActionField, {label: string; options: string[]}> = {
  priority: {label: 'Priority', options: BULK_ACTION_PRIORITY_OPTIONS},
  status: {label: 'Status', options: BULK_ACTION_STATUS_OPTIONS},
  category: {label: 'Category', options: BULK_ACTION_CATEGORY_OPTIONS}
};

const displayValue = (value: string) => value === UNCHANGED_OPTION ? `[${value}]` : value;
const valueClass = (field: BulkActionField, value: string) =>
  `bulk-action-control--${field}-${value.toLowerCase().replace(' ', '-')}`;

export const BulkActionModal: React.FC<BulkActionModalProps> = ({
  open,
  applying = false,
  onCancel,
  onApply
}) => {
  const [priority, setPriority] = useState(UNCHANGED_OPTION);
  const [status, setStatus] = useState('Resolved');
  const [category, setCategory] = useState(UNCHANGED_OPTION);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openField, setOpenField] = useState<BulkActionField | null>(null);
  const fieldSetters: Record<BulkActionField, (value: string) => void> = {
    priority: setPriority,
    status: setStatus,
    category: setCategory
  };

  useEffect(() => {
    if (open) {
      setPriority(UNCHANGED_OPTION);
      setStatus('Resolved');
      setCategory(UNCHANGED_OPTION);
    }
  }, [open]);

  const isAllUnchanged = priority === UNCHANGED_OPTION && status === UNCHANGED_OPTION
    && category === UNCHANGED_OPTION;
  const isApplyDisabled = isAllUnchanged || applying;

  const handleCancel = () => {
    if (!applying) {
      onCancel();
    }
  };

  const handleApply = () => {
    const changes: BulkActionChanges = {};
    if (priority !== UNCHANGED_OPTION) changes.priority = priority;
    if (status !== UNCHANGED_OPTION) changes.status = status;
    if (category !== UNCHANGED_OPTION) changes.category = category;
    onApply(changes);
  };

  const openMenu = (field: BulkActionField, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!applying) {
      setMenuAnchor(event.currentTarget);
      setOpenField(field);
    }
  };

  const selectOption = (field: BulkActionField, option: string) => {
    fieldSetters[field](option);
    setMenuAnchor(null);
    setOpenField(null);
  };

  const renderField = (field: BulkActionField, value: string) => {
    const {label, options} = BULK_ACTION_FIELDS[field];
    return (
      <div className='bulk-action-field'>
        <span className='bulk-action-label' id={`bulk-action-${field}-label`}>{label}:</span>
        <button
          aria-controls={openField === field ? `bulk-action-${field}-menu` : undefined}
          aria-expanded={openField === field}
          aria-haspopup='menu'
          aria-labelledby={`bulk-action-${field}-label bulk-action-${field}-value`}
          className={`bulk-action-control ${valueClass(field, value)}`}
          data-testid={`bulk-action-${field}`}
          disabled={applying}
          onClick={(event) => openMenu(field, event)}
          type='button'
        >
          <span id={`bulk-action-${field}-value`}>{displayValue(value)}</span>
        </button>
        <Menu
          anchorEl={menuAnchor}
          classes={{paper: 'bulk-action-menu'}}
          id={`bulk-action-${field}-menu`}
          MenuListProps={{'aria-label': `${label} options`}}
          onClose={() => {
            setMenuAnchor(null);
            setOpenField(null);
          }}
          open={openField === field}
        >
          {options.map((option) => <MenuItem
            aria-selected={option === value}
            className={`bulk-action-menu-option ${option === value
              ? 'bulk-action-menu-option--selected' : ''}`}
            data-testid={`bulk-action-${field}-${option}`}
            key={option}
            onClick={() => selectOption(field, option)}
            selected={option === value}
          >
            <span>{displayValue(option)}</span>
            {option === value && <CheckIcon className='bulk-action-menu-check' />}
          </MenuItem>)}
        </Menu>
      </div>
    );
  };

  return (
    <Dialog
      aria-describedby='bulk-action-description'
      aria-labelledby='bulk-action-title'
      className='bulk-action-dialog'
      id='modal-bulk-action'
      onClose={handleCancel}
      open={open}
      PaperProps={{className: 'bulk-action-dialog-paper'}}
    >
      <DialogTitle id='bulk-action-title'>
        {BULK_ACTION}
        <IconButton
          aria-label='close'
          data-testid='bulk-action-close'
          disabled={applying}
          onClick={handleCancel}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p id='bulk-action-description'>{BULK_ACTION_APPLIED_TO_ALL}</p>
        <div className='bulk-action-form'>
          {renderField('priority', priority)}
          {renderField('status', status)}
          {renderField('category', category)}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          backgroundColor='transparent'
          border='1px solid #e0e0e0'
          borderRadius='4px'
          color='#345c8b'
          cssClass='pure-material-button-like shadow-sm'
          dataTestId='modal-cancel-btn'
          disabled={applying}
          fontSize={14}
          fontWeight='600'
          height='40px'
          onClick={handleCancel}
          padding='8px 24px'
          title='Cancel'
          titleMargin='0'
          variant='outline'
          width='fit-content'
        />
        <Button
          backgroundColor={isApplyDisabled ? 'rgba(0, 0, 0, 0.12)' : '#345c8b'}
          borderRadius='4px'
          color={isApplyDisabled ? 'rgba(0, 0, 0, 0.38)' : '#fff'}
          cssClass='pure-material-button-like agree'
          dataTestId='modal-agree-btn'
          disabled={isApplyDisabled}
          fontSize={14}
          fontWeight='600'
          height='40px'
          margin='0 0 0 8px'
          onClick={handleApply}
          padding='8px 24px'
          title='Apply'
          titleMargin='0'
          variant='outline'
          width='fit-content'
        />
      </DialogActions>
    </Dialog>
  );
};
